import DragHandleIcon from '@mui/icons-material/DragHandle';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { styled } from '@mui/material/styles';
import { type JSX, useCallback, useState } from 'react';
import { ReactSortable } from 'react-sortablejs';
import { Checkbox } from '../Checkbox/Checkbox.tsx';

const FROZEN_ITEM_SELECTOR = '[data-frozen="true"]';

const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: prop => prop !== 'isFrozen',
})<{ isFrozen?: boolean }>`
  column-gap: ${({ theme }) => theme.spacing(3)};

  ${({ isFrozen }) =>
    isFrozen &&
    `
      cursor: default;
    `}
`;

const StyledCheckbox = styled(Checkbox)`
  padding-right: 0;
`;

const StyledDragHandleIcon = styled(DragHandleIcon)`
  cursor: grab;
`;

export interface FilterableItem {
  id: string;
  key: string;
  hide?: boolean;
  disabled?: boolean;
  freeze?: boolean;
  title?: React.ReactNode;
  label?: string;
}

export interface CustomizableListMenuProps<T extends FilterableItem> {
  items: T[];
  onItemsChange: (items: T[]) => void;
  getItemLabel: (item: T) => string;
  onItemClick?: (item: T) => void; // Optional click handler
  testIdPrefix?: string;
  buttonAriaLabel?: string;
}

export const CustomizableListMenu = <T extends FilterableItem>({
  items,
  onItemsChange,
  getItemLabel,
  onItemClick,
  testIdPrefix = 'column-visibility',
  buttonAriaLabel,
}: CustomizableListMenuProps<T>): JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleToggleVisibility = useCallback(
    (item: T) => {
      if (item.disabled) return;

      if (onItemClick) {
        onItemClick(item);
      } else {
        const newItems = items.map(i => (i.id === item.id ? { ...i, hide: !i.hide } : i));
        onItemsChange(newItems);
      }
    },
    [items, onItemsChange, onItemClick]
  );

  const handleMove = useCallback((event: { related: HTMLElement | null }) => {
    return !event.related?.closest(FROZEN_ITEM_SELECTOR);
  }, []);

  return (
    <>
      <IconButton onClick={handleClick} size="small" data-test={`${testIdPrefix}-button`} aria-label={buttonAriaLabel}>
        <ViewColumnIcon data-test={`${testIdPrefix}-icon`} />
      </IconButton>
      <Menu
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'bottom',
        }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        data-test={`${testIdPrefix}-menu`}
      >
        <ReactSortable
          list={items}
          setList={onItemsChange}
          swapThreshold={0.65}
          animation={300}
          handle=".customizable-list-menu-drag-handle"
          filter={FROZEN_ITEM_SELECTOR}
          preventOnFilter={false}
          onMove={handleMove}
        >
          {items.map(item => {
            const isFrozen = !!item.freeze;

            return (
              <StyledMenuItem
                data-frozen={isFrozen ? true : undefined}
                data-test={`${testIdPrefix}-item-${item.title ?? item.key}`}
                isFrozen={isFrozen}
                key={item.id}
                onClick={() => handleToggleVisibility(item)}
              >
                <StyledCheckbox
                  data-test={`${testIdPrefix}-checkbox`}
                  checked={!item.hide}
                  disabled={!!item.disabled}
                />
                <ListItemText primary={getItemLabel(item)} data-test={`${testIdPrefix}-name`} />
                {!isFrozen && (
                  <StyledDragHandleIcon
                    className="customizable-list-menu-drag-handle"
                    data-test={`${testIdPrefix}-drag-icon`}
                  />
                )}
              </StyledMenuItem>
            );
          })}
        </ReactSortable>
      </Menu>
    </>
  );
};
